
import React from 'react';
import { BrainCircuit, ListChecks, MessagesSquare, RotateCcw, Lightbulb } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArticleAIContextType, useArticleAI } from '@/contexts/article-ai/ArticleAIContext';
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger 
} from '@/components/ui/tooltip';

type AIFeatureButton = {
  id: ArticleAIContextType['activeFeature'];
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  condition?: boolean;
  highlights?: boolean;
};

interface ArticleAIControlsProps {
  isOpinionPiece?: boolean;
  contentComplexity?: number;
  showLabels?: boolean;
}

const ArticleAIControls: React.FC<ArticleAIControlsProps> = ({ 
  isOpinionPiece = false, 
  contentComplexity = 0, 
  showLabels = true 
}) => {
  const { activeFeature, setActiveFeature } = useArticleAI();

  const featureButtons: AIFeatureButton[] = [
    {
      id: 'takeaways',
      icon: ListChecks,
      title: 'Key Takeaways',
      description: 'Get the main points from this article'
    },
    {
      id: 'ask',
      icon: MessagesSquare,
      title: 'Ask About This',
      description: 'Ask questions about the article content'
    },
    {
      id: 'simplify',
      icon: Lightbulb,
      title: 'Explain Simply',
      description: 'Simplify complex concepts in this article',
      condition: contentComplexity > 0.5
    },
    {
      id: 'perspective',
      icon: RotateCcw,
      title: 'Different Perspective',
      description: 'See an alternative viewpoint on this topic',
      condition: isOpinionPiece,
      highlights: true // Add highlight for new or important features
    }
  ];

  // Filter buttons based on conditions
  const visibleButtons = featureButtons.filter(
    button => button.condition === undefined || button.condition
  );

  return (
    <div className="article-ai-controls flex flex-col space-y-2 mb-4">
      <div className="flex items-center mb-2">
        <BrainCircuit className="h-4 w-4 mr-2 text-primary" />
        <Badge variant="outline" className="bg-primary/10 text-primary text-xs">
          AI TOOLS
        </Badge>
      </div>
      
      <div className="flex flex-wrap gap-2">
        {visibleButtons.map((button) => (
          <TooltipProvider key={button.id}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={activeFeature === button.id ? "default" : "outline"}
                  size="sm"
                  className={`flex items-center ${button.highlights ? 'border-yellow-400 dark:border-yellow-500 shadow-sm' : ''} ${button.highlights && !activeFeature ? 'animate-gentle-pulse' : ''}`}
                  onClick={() => setActiveFeature(activeFeature === button.id ? null : button.id)}
                >
                  <button.icon className={`h-4 w-4 mr-2 ${button.highlights ? 'text-yellow-600 dark:text-yellow-400' : ''}`} />
                  {showLabels && button.title}
                  {button.highlights && (
                    <span className="ml-1 w-2 h-2 rounded-full bg-yellow-400"></span>
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs text-xs p-2">
                <p>{button.description}</p>
                {button.id === 'perspective' && (
                  <p className="mt-1 text-yellow-600 dark:text-yellow-400 font-semibold">
                    See how others might view this topic differently!
                  </p>
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ))}
      </div>
    </div>
  );
};

export default ArticleAIControls;
